import { trackDownload } from '../utils/downloadIndicator';
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Tooltip,
  Alert,
  Card,
  CardContent,
  CardActions,
  useMediaQuery,
  useTheme,
  Divider,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SearchIcon from '@mui/icons-material/Search';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryIcon from '@mui/icons-material/History';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { itemsApi, pdfExportApi, quoteAssistApi, savedItemsApi, surveyNotesApi } from '../services/api';
import { clearQuoteAssistRequestImages } from './projectItemsTabQuoteAssist';

// Sortable Table Row Component
function SortableTableRow({ id, children, isDragging, dragHandleProps, ...props }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <TableRow ref={setNodeRef} style={style} {...props}>
      {React.Children.map(children, (child, index) => {
        // Pass drag props to the first cell (drag handle)
        if (index === 0 && React.isValidElement(child)) {
          return React.cloneElement(child, {
            ...child.props,
            children: React.cloneElement(child.props.children, {
              ...attributes,
              ...listeners,
            })
          });
        }
        return child;
      })}
    </TableRow>
  );
}

// Sortable Card Component for Mobile
function SortableCard({ id, children, isDragging, ...props }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box ref={setNodeRef} style={style} {...props}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
        }}
      >
        <Box
          {...attributes}
          {...listeners}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'grab',
            pt: 1,
            '&:active': {
              cursor: 'grabbing',
            },
          }}
        >
          <DragIndicatorIcon color="action" />
        </Box>
        <Box sx={{ flex: 1 }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
}

function ProjectItemsTab({
  projectId,
  items,
  projectStatus,
  project,
  refreshItems,
  onSurveyNotesDirtyChange,
  onSurveyNotesSaved,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // 'create' or 'edit'
  const [currentItem, setCurrentItem] = useState(null);
  const [itemData, setItemData] = useState({
    name: '',
    quantity: '',
    list_price: '',
    units: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Saved items search state
  const [savedItemsSearch, setSavedItemsSearch] = useState('');
  const [savedItems, setSavedItems] = useState([]);
  const [savedItemsLoading, setSavedItemsLoading] = useState(false);
  const [showSavedItems, setShowSavedItems] = useState(false);
  
  // Survey Notes state - initialize from project prop
  const [surveyNotes, setSurveyNotes] = useState(project?.survey_notes || '');
  const [surveyNotesLoading, setSurveyNotesLoading] = useState(false);
  const [surveyNotesError, setSurveyNotesError] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [surveyNotesSavedMessage, setSurveyNotesSavedMessage] = useState('');
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [surveyNotesHistory, setSurveyNotesHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [historyDialogEntry, setHistoryDialogEntry] = useState(null);

  // Quote Assist state
  const [quoteAssistOpen, setQuoteAssistOpen] = useState(false);
  const [quoteAssistLoading, setQuoteAssistLoading] = useState(false);
  const [quoteAssistMode, setQuoteAssistMode] = useState('assist');
  const [quoteAssistStep, setQuoteAssistStep] = useState('request');
  const [quoteAssistError, setQuoteAssistError] = useState(null);
  const [quoteReviewSummary, setQuoteReviewSummary] = useState('');
  const [quoteReviewSuggestions, setQuoteReviewSuggestions] = useState([]);
  const [quoteReviewStatuses, setQuoteReviewStatuses] = useState({});
  const [quoteReviewApplying, setQuoteReviewApplying] = useState({});
  const [quoteAssistContext, setQuoteAssistContext] = useState({
    request_text: '',
    request_images: [],
    answers: {},
    example_items: [],
    project_context: {
      organisation_name: '',
      quote_reference: '',
    },
    questions: [],
  });
  const [quoteAssistQuestions, setQuoteAssistQuestions] = useState([]);
  const [quoteAssistAnswers, setQuoteAssistAnswers] = useState({});
  const [quoteAssistCustomModes, setQuoteAssistCustomModes] = useState({});
  const [quoteAssistIsListening, setQuoteAssistIsListening] = useState(false);
  const [quoteAssistSpeechSupported, setQuoteAssistSpeechSupported] = useState(false);
  const [quoteAssistSpeechError, setQuoteAssistSpeechError] = useState(null);
  const [quoteAssistLoadingPhase, setQuoteAssistLoadingPhase] = useState(null);
  const quoteAssistRecognitionRef = React.useRef(null);

  const buildInitialQuoteAssistContext = (requestText = '') => ({
    request_text: requestText,
    request_images: [],
    answers: {},
    example_items: [],
    project_context: {
      organisation_name: '',
      quote_reference: '',
    },
    questions: [],
  });

  // Survey Notes speech state
  const [surveyNotesIsListening, setSurveyNotesIsListening] = useState(false);
  const [surveyNotesSpeechSupported, setSurveyNotesSpeechSupported] = useState(false);
  const [surveyNotesSpeechError, setSurveyNotesSpeechError] = useState(null);
  const surveyNotesRecognitionRef = React.useRef(null);

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    setQuoteAssistSpeechSupported(Boolean(SpeechRecognition));
    setSurveyNotesSpeechSupported(Boolean(SpeechRecognition));
  }, []);

  useEffect(() => {
    return () => {
      if (quoteAssistRecognitionRef.current) {
        quoteAssistRecognitionRef.current.stop();
        quoteAssistRecognitionRef.current = null;
      }
      if (surveyNotesRecognitionRef.current) {
        surveyNotesRecognitionRef.current.stop();
        surveyNotesRecognitionRef.current = null;
      }
    };
  }, []);

  // Update survey notes when project changes
  useEffect(() => {
    if (!hasUnsavedChanges && project?.survey_notes !== undefined) {
      setSurveyNotes(project.survey_notes || '');
    }
  }, [project?.survey_notes, hasUnsavedChanges]);

  useEffect(() => {
    if (onSurveyNotesDirtyChange) {
      onSurveyNotesDirtyChange(hasUnsavedChanges);
    }
  }, [hasUnsavedChanges, onSurveyNotesDirtyChange]);

  useEffect(() => () => {
    if (onSurveyNotesDirtyChange) {
      onSurveyNotesDirtyChange(false);
    }
  }, [onSurveyNotesDirtyChange]);

  // PDF Export state
  const [termsDialogOpen, setTermsDialogOpen] = useState(false);
  const [customTerms, setCustomTerms] = useState('');
  const [defaultTerms, setDefaultTerms] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const [loadingTerms, setLoadingTerms] = useState(false);

  // Drag and drop state
  const [draggedItem, setDraggedItem] = useState(null);
  const [reorderLoading, setReorderLoading] = useState(false);

  // Hide prices state - persist in localStorage across pages
  const [hidesPrices, setHidesPrices] = useState(() => {
    try {
      const saved = localStorage.getItem('projects_hideValues');
      return saved ? JSON.parse(saved) : false;
    } catch (e) {
      console.warn('Could not read hideValues from localStorage', e);
      return false;
    }
  });

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Load default terms and conditions ONLY when needed (lazy load for performance)
  const loadDefaultTermsIfNeeded = async () => {
    if (defaultTerms) return defaultTerms; // Already loaded, return it
    
    try {
      setLoadingTerms(true);
      const response = await pdfExportApi.getDefaultTerms();
      const terms = response.data.terms_and_conditions;
      setDefaultTerms(terms);
      return terms;
    } catch (error) {
      console.error('Error loading default terms:', error);
      return '';
    } finally {
      setLoadingTerms(false);
    }
  };

  // Handle PDF export based on project status
  const handlePdfExport = async () => {
    if (items.length === 0) {
      alert('No items to export');
      return;
    }

    if (projectStatus === 'Quote') {
      // Load terms before showing dialog
      const terms = await loadDefaultTermsIfNeeded();
      // For quotes, show terms dialog with loaded terms
      setCustomTerms(terms);
      setTermsDialogOpen(true);
    } else {
      // For sales orders, export directly (no terms needed)
      handleSalesOrderExport();
    }
  };

  // Handle sales order export (no terms needed)
  const handleSalesOrderExport = trackDownload(async () => {
    try {
      setExportLoading(true);
      const response = await pdfExportApi.exportSalesOrder(projectId);
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Try to get filename from headers (prefer X-Filename), fallback to parsing Content-Disposition
      const headerFilename = response.headers['x-filename'];
      let filename = headerFilename || `SO${projectId}.pdf`;
      if (!headerFilename) {
        const cd = response.headers['content-disposition'];
        if (cd) {
          // Try RFC 5987 filename*
          const fnStar = cd.match(/filename\*=UTF-8''([^;\n]+)/i);
          const fnBasic = cd.match(/filename="?([^";\n]+)"?/i);
          if (fnStar && fnStar[1]) {
            try { filename = decodeURIComponent(fnStar[1]); } catch { filename = fnStar[1]; }
          } else if (fnBasic && fnBasic[1]) {
            filename = fnBasic[1];
          }
        }
      }
      link.setAttribute('download', filename);      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error exporting sales order:', error);
      alert(error.response?.data?.error || 'Failed to export sales order PDF');
    } finally {
      setExportLoading(false);
    }
  });

  // Handle quote PDF generation with custom terms
  const handleQuotePdfGeneration = trackDownload(async () => {
    try {
      setExportLoading(true);
      const response = await pdfExportApi.exportQuote(projectId, {
        terms_and_conditions: customTerms
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Try to get filename from headers (prefer X-Filename), fallback to parsing Content-Disposition
      const headerFilename = response.headers['x-filename'];
      let filename = headerFilename || `QUO${projectId}.pdf`;
      if (!headerFilename) {
        const cd = response.headers['content-disposition'];
        if (cd) {
          const fnStar = cd.match(/filename\*=UTF-8''([^;\n]+)/i);
          const fnBasic = cd.match(/filename="?([^";\n]+)"?/i);
          if (fnStar && fnStar[1]) {
            try { filename = decodeURIComponent(fnStar[1]); } catch { filename = fnStar[1]; }
          } else if (fnBasic && fnBasic[1]) {
            filename = fnBasic[1];
          }
        }
      }
      link.setAttribute('download', filename);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setTermsDialogOpen(false);
      
    } catch (error) {
      console.error('Error exporting quote:', error);
      alert(error.response?.data?.error || 'Failed to export quote PDF');
    } finally {
      setExportLoading(false);
    }
  });

  // Handle terms dialog close
  const handleTermsDialogClose = () => {
    setTermsDialogOpen(false);
    setCustomTerms(defaultTerms); // Reset to default
  };

  // Handle custom terms change
  const handleCustomTermsChange = (event) => {
    setCustomTerms(event.target.value);
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount);
  };
  
  // Calculate total for a line item
  const calculateLineTotal = (item) => {
    return item.quantity * item.list_price;
  };
  
  // Calculate net total (before tax)
  const calculateNetTotal = () => {
    return items.reduce((total, item) => total + calculateLineTotal(item), 0);
  };
  
  // Calculate tax (20% VAT - this can be made configurable later)
  const calculateTax = () => {
    return calculateNetTotal() * 0.2;
  };
  
  // Calculate grand total (net + tax)
  const calculateGrandTotal = () => {
    return calculateNetTotal() + calculateTax();
  };

  // Drag and drop handlers
  const handleDragStart = (event) => {
    const { active } = event;
    const draggedItem = items.find(item => item.item_id === active.id);
    setDraggedItem(draggedItem);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setDraggedItem(null);

    if (!over || active.id === over.id) {
      return;
    }

    // Only allow reordering if editing is allowed
    if (!isEditingAllowed) {
      return;
    }

    const activeIndex = items.findIndex(item => item.item_id === active.id);
    const overIndex = items.findIndex(item => item.item_id === over.id);

    if (activeIndex !== overIndex) {
      try {
        setReorderLoading(true);
        
        // Create new order array
        const newItems = arrayMove(items, activeIndex, overIndex);
        
        // Create order update payload
        const itemOrders = newItems.map((item, index) => ({
          item_id: item.item_id,
          order_index: index + 1
        }));

        // Update order in backend
        await itemsApi.bulkReorder(projectId, { item_orders: itemOrders });
        
        // Refresh items to reflect new order
        await refreshItems();
        
      } catch (error) {
        console.error('Error reordering items:', error);
        alert(error.response?.data?.error || 'Failed to reorder items');
      } finally {
        setReorderLoading(false);
      }
    }
  };

  // Mobile item card component
  const ItemCard = ({ item }) => (
    <SortableCard key={item.item_id} id={item.item_id} isDragging={draggedItem?.item_id === item.item_id}>
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Typography 
              variant="h6" 
              component="div" 
              sx={{ 
                fontSize: '1rem', 
                fontWeight: 600,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                flex: 1,
                mr: 1
              }}
            >
              {item.name}
            </Typography>
            {!hidesPrices && (
              <Chip 
                label={formatCurrency(calculateLineTotal(item))}
                color="primary"
                size="small"
              />
            )}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            <strong>Quantity:</strong> {item.quantity} {item.units && `(${item.units})`}
          </Typography>
          {!hidesPrices && (
            <Typography variant="body2" color="text.secondary">
              <strong>Price:</strong> {formatCurrency(item.list_price)}
            </Typography>
          )}
        </CardContent>
        {isEditingAllowed && (
          <CardActions sx={{ pt: 0, pb: 2, px: 2, gap: 1 }}>
            <Button
              size="small"
              startIcon={<EditIcon />}
              onClick={() => handleEditItem(item)}
            >
              Edit
            </Button>
            <Button
              size="small"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => handleDeleteItem(item)}
            >
              Delete
            </Button>
          </CardActions>
        )}
      </Card>
    </SortableCard>
  );

  // Open dialog to create new item
  const handleAddItem = () => {
    setDialogMode('create');
    setItemData({
      name: '',
      quantity: '',
      list_price: '',
      units: '',
    });
    setCurrentItem(null);
    setSavedItemsSearch('');
    setSavedItems([]);
    setShowSavedItems(false);
    setDialogOpen(true);
  };
  
  // Open dialog to edit existing item
  const handleEditItem = (item) => {
    setDialogMode('edit');
    setItemData({
      name: item.name,
      quantity: item.quantity,
      list_price: item.list_price,
      units: item.units || '',
    });
    setCurrentItem(item);
    setSavedItemsSearch('');
    setSavedItems([]);
    setShowSavedItems(false);
    setDialogOpen(true);
  };
  
  // Handle item data change
  const handleItemDataChange = (event) => {
    const { name, value } = event.target;
    setItemData({
      ...itemData,
      [name]: value,
    });
  };
  
  // Handle saved items search
  const handleSavedItemsSearch = async (searchQuery) => {
    setSavedItemsSearch(searchQuery);
    
    if (searchQuery.trim().length === 0) {
      setSavedItems([]);
      setShowSavedItems(false);
      return;
    }
    
    if (searchQuery.trim().length < 2) {
      setShowSavedItems(false);
      return;
    }
    
    try {
      setSavedItemsLoading(true);
      const response = await savedItemsApi.search(searchQuery);
      setSavedItems(response.data);
      setShowSavedItems(true);
    } catch (error) {
      console.error('Error searching saved items:', error);
      setSavedItems([]);
      setShowSavedItems(false);
    } finally {
      setSavedItemsLoading(false);
    }
  };
  
  // Handle saved item selection
  const handleSavedItemSelect = (savedItem) => {
    setItemData({
      ...itemData,
      name: savedItem.name,
      units: savedItem.units,
    });
    setSavedItemsSearch('');
    setSavedItems([]);
    setShowSavedItems(false);
  };
  
  // Handle dialog close
  const handleDialogClose = () => {
    setDialogOpen(false);
    setError(null);
    setSavedItemsSearch('');
    setSavedItems([]);
    setShowSavedItems(false);
  };
  
  // Handle save button click
  const handleSaveItem = async () => {
    // Validate form inputs
    const quantity = parseFloat(itemData.quantity);
    const listPrice = parseFloat(itemData.list_price);
    
    if (!itemData.name) {
      setError('Name is required');
      return;
    }
    
    if (!itemData.units) {
      setError('Units are required');
      return;
    }
    
    if (isNaN(quantity) || quantity <= 0) {
      setError('Quantity must be a positive number');
      return;
    }
    
    if (isNaN(listPrice) || listPrice < 0) {
      setError('Price must be a non-negative number');
      return;
    }
    
    // Prepare payload
    const payload = {
      name: itemData.name,
      units: itemData.units,
      quantity: quantity,
      list_price: listPrice,
    };
    
    try {
      setLoading(true);
      
      if (dialogMode === 'create') {
        // Create new item
        await itemsApi.create(projectId, payload);
      } else {
        // Update existing item
        await itemsApi.update(currentItem.item_id, payload);
      }
      
      // Refresh items list
      await refreshItems();
      
      // Close dialog
      handleDialogClose();
    } catch (err) {
      console.error('Error saving project item:', err);
      setError(err.response?.data?.error || 'Failed to save project item');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle delete item
  const handleDeleteItem = async (item) => {
    if (!window.confirm(`Are you sure you want to delete ${item.name}?`)) {
      return;
    }
    
    try {
      setLoading(true);
      await itemsApi.delete(item.item_id);
      
      // Refresh items list
      await refreshItems();
    } catch (err) {
      console.error('Error deleting project item:', err);
      alert(err.response?.data?.error || 'Failed to delete project item');
    } finally {
      setLoading(false);
    }
  };
  
  // Check if editing is allowed (for Quote and Sales Order statuses)
  const isEditingAllowed = projectStatus === 'Quote' || projectStatus === 'Sales Order';

  const handleSurveyNotesChange = (event) => {
    setSurveyNotes(event.target.value);
    setHasUnsavedChanges(true);
    setSurveyNotesError(null);
    setSurveyNotesSavedMessage('');
  };

  const saveSurveyNotes = async () => {
    if (!hasUnsavedChanges) return;
    
    try {
      setSurveyNotesLoading(true);
      setSurveyNotesError(null);

      const response = await surveyNotesApi.save(
        projectId,
        surveyNotes,
        isMobile ? 'mobile' : 'desktop'
      );

      if (onSurveyNotesSaved) {
        onSurveyNotesSaved(response.data.notes);
      }
      setHasUnsavedChanges(false);
      setSurveyNotesSavedMessage('Saved successfully');

      if (historyLoaded) {
        setSurveyNotesHistory((current) => [
          response.data.history_entry,
          ...current.filter(
            (entry) => entry.history_id !== response.data.history_entry.history_id
          ),
        ]);
      }
    } catch (error) {
      console.error('Error saving survey notes:', error);
      setSurveyNotesError(error.response?.data?.error || 'Failed to save survey notes. Please try again.');
    } finally {
      setSurveyNotesLoading(false);
    }
  };

  const loadSurveyNotesHistory = async () => {
    try {
      setHistoryLoading(true);
      setHistoryError(null);
      const response = await surveyNotesApi.getHistory(projectId);
      setSurveyNotesHistory(response.data);
      setHistoryLoaded(true);
    } catch (error) {
      console.error('Error loading survey note history:', error);
      setHistoryError('Could not load version history. Please try again.');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleHistoryExpandedChange = (_event, expanded) => {
    setHistoryExpanded(expanded);
    if (expanded && !historyLoaded && !historyLoading) {
      loadSurveyNotesHistory();
    }
  };

  const formatHistoryDate = (value) => {
    if (!value) return 'Unknown time';
    return new Date(value).toLocaleString();
  };

  const resetQuoteAssistState = () => {
    if (quoteAssistRecognitionRef.current) {
      quoteAssistRecognitionRef.current.stop();
      quoteAssistRecognitionRef.current = null;
    }
    setQuoteAssistIsListening(false);
    setQuoteAssistSpeechError(null);
    setQuoteAssistLoadingPhase(null);
    setQuoteAssistOpen(false);
    setQuoteAssistLoading(false);
    setQuoteAssistMode('assist');
    setQuoteAssistStep('request');
    setQuoteAssistError(null);
    setQuoteReviewSummary('');
    setQuoteReviewSuggestions([]);
    setQuoteReviewStatuses({});
    setQuoteReviewApplying({});
    setQuoteAssistQuestions([]);
    setQuoteAssistAnswers({});
    setQuoteAssistCustomModes({});
    setQuoteAssistContext(buildInitialQuoteAssistContext());
  };

  const openQuoteAssist = async () => {
    try {
      const initialRequestText = surveyNotes || project?.survey_notes || '';

      setQuoteAssistLoading(true);
      setQuoteAssistLoadingPhase(items.length > 0 ? 'review' : 'context');
      setQuoteAssistOpen(true);
      setQuoteAssistError(null);

      if (items.length > 0) {
        setQuoteAssistMode('review');
        setQuoteAssistStep('generating');

        const response = await quoteAssistApi.review(projectId);
        const data = response?.data || {};

        setQuoteReviewSummary(data.overall_summary || 'No major issues found in this quote.');
        setQuoteReviewSuggestions(Array.isArray(data.suggestions) ? data.suggestions : []);
        setQuoteReviewStatuses({});
        setQuoteReviewApplying({});
        setQuoteAssistStep('questions');
        return;
      }

      setQuoteAssistMode('assist');
      setQuoteAssistStep('request');
      setQuoteReviewSummary('');
      setQuoteReviewSuggestions([]);
      setQuoteReviewStatuses({});
      setQuoteReviewApplying({});
      setQuoteAssistQuestions([]);
      setQuoteAssistAnswers({});
      setQuoteAssistCustomModes({});
      setQuoteAssistContext(buildInitialQuoteAssistContext(initialRequestText));

      const response = await quoteAssistApi.getContext(projectId);
      const data = response?.data || {};
      const nextContext = data.context || data;

      setQuoteAssistContext((prev) => {
        const previousRequestText = typeof prev?.request_text === 'string' ? prev.request_text : '';
        const requestText =
          previousRequestText !== initialRequestText
            ? previousRequestText
            : nextContext.request_text || initialRequestText;

        return {
          request_text: requestText,
          request_images: Array.isArray(nextContext.request_images) ? nextContext.request_images : [],
          answers: nextContext.answers || {},
          example_items: Array.isArray(nextContext.example_items) ? nextContext.example_items : [],
          project_context: nextContext.project_context || {
            organisation_name: '',
            quote_reference: '',
          },
          questions: Array.isArray(nextContext.questions) ? nextContext.questions : [],
        };
      });
      setQuoteAssistStep('request');
    } catch (err) {
      console.error('Error loading quote assist:', err);
      setQuoteAssistError(
        err.response?.data?.error || (items.length > 0 ? 'Failed to load Quote Review' : 'Failed to load Quote Assist')
      );
      if (items.length === 0) {
        setQuoteAssistStep('request');
      }
    } finally {
      setQuoteAssistLoadingPhase(null);
      setQuoteAssistLoading(false);
    }
  };

  const removeQuoteAssistImage = (indexToRemove) => {
    setQuoteAssistContext((prev) => ({
      ...prev,
      request_images: (prev.request_images || []).filter((_, index) => index !== indexToRemove),
    }));
  };

  const clearQuoteAssistImages = () => {
    setQuoteAssistContext((prev) => clearQuoteAssistRequestImages(prev));
  };

  const addQuoteAssistImage = async (event) => {
    const files = event.target.files || [];
    if (files.length === 0) return;

    try {
      setQuoteAssistError(null);

      const processFiles = Array.from(files).map(async (file) => {
        if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
          const formData = new FormData();
          formData.append('file', file);
          const response = await quoteAssistApi.convertPdf(projectId, formData);
          return Array.isArray(response?.data?.images) ? response.data.images : [];
        }

        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            resolve([
              {
                file_name: file.name,
                data_url: e.target?.result || '',
                source: 'uploaded',
              },
            ]);
          };
          reader.readAsDataURL(file);
        });
      });

      const newImages = (await Promise.all(processFiles)).flat();
      setQuoteAssistContext((prev) => ({
        ...prev,
        request_images: [...(prev.request_images || []), ...newImages],
      }));
    } catch (err) {
      console.error('Error processing quote assist files:', err);
      setQuoteAssistError(err.response?.data?.error || 'Failed to process selected files');
    } finally {
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const startQuoteAssist = async () => {
    try {
      setQuoteAssistLoading(true);
      setQuoteAssistLoadingPhase('questions');
      setQuoteAssistError(null);

      const response = await quoteAssistApi.start(projectId, {
        request_text: quoteAssistContext.request_text,
        request_images: quoteAssistContext.request_images,
      });

      const result = response?.data || {};
      const nextContext = result.context || quoteAssistContext;
      const nextQuestions = result.questions || [];

      setQuoteAssistContext(nextContext);
      setQuoteAssistQuestions(nextQuestions);
      setQuoteAssistAnswers(nextContext.answers || {});
      setQuoteAssistCustomModes({});

      if (nextQuestions.length > 0) {
        setQuoteAssistStep('questions');
      } else {
        await generateQuoteAssistItems(nextContext);
      }
    } catch (err) {
      console.error('Error starting quote assist:', err);
      setQuoteAssistError(err.response?.data?.error || 'Failed to start Quote Assist');
    } finally {
      setQuoteAssistLoadingPhase(null);
      setQuoteAssistLoading(false);
    }
  };

  const updateQuoteAnswer = (questionId, value) => {
    setQuoteAssistAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const stopQuoteAssistListening = () => {
    if (quoteAssistRecognitionRef.current) {
      quoteAssistRecognitionRef.current.stop();
      quoteAssistRecognitionRef.current = null;
    }
    setQuoteAssistIsListening(false);
  };

  const startQuoteAssistListening = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setQuoteAssistSpeechError('Speech recognition is not supported on this browser.');
      return;
    }

    setQuoteAssistSpeechError(null);
    const recognition = new SpeechRecognition();
    recognition.lang = 'en-GB';
    recognition.continuous = true;
    recognition.interimResults = true;

    const initialText = quoteAssistContext.request_text || '';
    let accumulatedFinalTranscript = '';
    recognition.onresult = (event) => {
      let newFinalTranscript = '';
      let interimTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const transcriptPart = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          newFinalTranscript += transcriptPart;
        } else {
          interimTranscript += transcriptPart;
        }
      }

      const normalizedNewFinal = newFinalTranscript.trim();
      if (normalizedNewFinal) {
        accumulatedFinalTranscript = [accumulatedFinalTranscript, normalizedNewFinal]
          .filter(Boolean)
          .join(accumulatedFinalTranscript ? ' ' : '');
      }

      const mergedFinal = [initialText, accumulatedFinalTranscript]
        .filter(Boolean)
        .join(initialText && accumulatedFinalTranscript ? ' ' : '');
      const normalizedInterim = interimTranscript.trim();
      const mergedWithInterim = [mergedFinal, normalizedInterim]
        .filter(Boolean)
        .join(mergedFinal && normalizedInterim ? ' ' : '');

      setQuoteAssistContext((prev) => ({
        ...prev,
        request_text: mergedWithInterim,
      }));
    };

    recognition.onerror = (event) => {
      if (event.error === 'not-allowed') {
        setQuoteAssistSpeechError('Microphone permission was denied. Please allow microphone access and try again.');
      } else if (event.error !== 'aborted') {
        setQuoteAssistSpeechError('Could not transcribe audio. Please try again.');
      }
    };

    recognition.onend = () => {
      setQuoteAssistIsListening(false);
      quoteAssistRecognitionRef.current = null;
    };

    recognition.start();
    quoteAssistRecognitionRef.current = recognition;
    setQuoteAssistIsListening(true);
  };

  const toggleQuoteAssistListening = () => {
    if (quoteAssistIsListening) {
      stopQuoteAssistListening();
      return;
    }
    startQuoteAssistListening();
  };

  // Survey Notes Speech Functions
  const stopSurveyNotesListening = () => {
    if (surveyNotesRecognitionRef.current) {
      surveyNotesRecognitionRef.current.stop();
      surveyNotesRecognitionRef.current = null;
    }
    setSurveyNotesIsListening(false);
  };

  const startSurveyNotesListening = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setSurveyNotesSpeechError('Speech recognition is not supported on this browser.');
      return;
    }

    setSurveyNotesSpeechError(null);
    const recognition = new SpeechRecognition();
    recognition.lang = 'en-GB';
    recognition.continuous = true;
    recognition.interimResults = true;

    const initialText = surveyNotes || '';
    let accumulatedFinalTranscript = '';
    recognition.onresult = (event) => {
      let newFinalTranscript = '';
      let interimTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const transcriptPart = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          newFinalTranscript += transcriptPart;
        } else {
          interimTranscript += transcriptPart;
        }
      }

      const normalizedNewFinal = newFinalTranscript.trim();
      if (normalizedNewFinal) {
        accumulatedFinalTranscript = [accumulatedFinalTranscript, normalizedNewFinal]
          .filter(Boolean)
          .join(accumulatedFinalTranscript ? ' ' : '');
      }

      const mergedFinal = [initialText, accumulatedFinalTranscript]
        .filter(Boolean)
        .join(initialText && accumulatedFinalTranscript ? ' ' : '');
      const normalizedInterim = interimTranscript.trim();
      const mergedWithInterim = [mergedFinal, normalizedInterim]
        .filter(Boolean)
        .join(mergedFinal && normalizedInterim ? ' ' : '');

      setSurveyNotes(mergedWithInterim);
      setHasUnsavedChanges(true);
    };

    recognition.onerror = (event) => {
      if (event.error === 'not-allowed') {
        setSurveyNotesSpeechError('Microphone permission was denied. Please allow microphone access and try again.');
      } else if (event.error !== 'aborted') {
        setSurveyNotesSpeechError('Could not transcribe audio. Please try again.');
      }
    };

    recognition.onend = () => {
      setSurveyNotesIsListening(false);
      surveyNotesRecognitionRef.current = null;
    };

    recognition.start();
    surveyNotesRecognitionRef.current = recognition;
    setSurveyNotesIsListening(true);
  };

  const toggleSurveyNotesListening = () => {
    if (surveyNotesIsListening) {
      stopSurveyNotesListening();
      return;
    }
    startSurveyNotesListening();
  };

  const continueQuoteAssist = async () => {
    try {
      setQuoteAssistLoading(true);
      setQuoteAssistError(null);

      const response = await quoteAssistApi.continueQuestions(projectId, {
        context: quoteAssistContext,
        answers: quoteAssistAnswers,
      });

      const result = response?.data || {};
      const nextContext = result.context || quoteAssistContext;
      const nextQuestions = result.questions || [];

      setQuoteAssistContext(nextContext);
      setQuoteAssistQuestions(nextQuestions);
      setQuoteAssistCustomModes((prevModes) => {
        const nextModes = { ...prevModes };
        (nextQuestions || []).forEach((question) => {
          if (question.type !== 'select') return;
          const questionValue = quoteAssistAnswers[question.id];
          const options = question.options || [];
          if (questionValue && !options.includes(questionValue)) {
            nextModes[question.id] = true;
          }
        });
        return nextModes;
      });

      if (nextQuestions.length > 0) {
        setQuoteAssistStep('questions');
      } else {
        await generateQuoteAssistItems(nextContext);
      }
    } catch (err) {
      console.error('Error continuing quote assist:', err);
      setQuoteAssistError(err.response?.data?.error || 'Failed to continue Quote Assist');
    } finally {
      setQuoteAssistLoading(false);
    }
  };

  const generateQuoteAssistItems = async (contextToUse = quoteAssistContext) => {
    try {
      setQuoteAssistLoading(true);
      setQuoteAssistStep('generating');
      setQuoteAssistError(null);

      const response = await quoteAssistApi.generate(projectId, {
        context: contextToUse,
      });

      const generatedCount = response?.data?.generated_count || 0;
      await refreshItems();
      alert(`Quote Assist added ${generatedCount} item${generatedCount === 1 ? '' : 's'}.`);
      resetQuoteAssistState();
    } catch (err) {
      console.error('Error generating quote items:', err);
      setQuoteAssistError(err.response?.data?.error || 'Failed to generate quote items');
      setQuoteAssistStep('questions');
    } finally {
      setQuoteAssistLoading(false);
    }
  };

  const formatReviewValue = (value, fallback = '-') => {
    if (value === null || value === undefined || value === '') return fallback;
    return value;
  };

  const applyQuoteReviewSuggestion = async (suggestion, index) => {
    try {
      setQuoteReviewApplying((prev) => ({ ...prev, [index]: true }));
      setQuoteAssistError(null);

      if (suggestion.action === 'update') {
        const targetItemId = suggestion?.before?.item_id;
        if (!targetItemId || !suggestion.after) {
          throw new Error('Invalid update suggestion payload');
        }

        await itemsApi.update(targetItemId, {
          name: suggestion.after.name,
          units: suggestion.after.units,
          quantity: Number(suggestion.after.quantity),
          list_price: Number(suggestion.after.list_price),
        });
      } else if (suggestion.action === 'remove') {
        const targetItemId = suggestion?.before?.item_id;
        if (!targetItemId) {
          throw new Error('Invalid remove suggestion payload');
        }

        await itemsApi.delete(targetItemId);
      } else if (suggestion.action === 'add') {
        if (!suggestion.after) {
          throw new Error('Invalid add suggestion payload');
        }

        await itemsApi.create(projectId, {
          name: suggestion.after.name,
          units: suggestion.after.units,
          quantity: Number(suggestion.after.quantity),
          list_price: Number(suggestion.after.list_price),
        });
      }

      await refreshItems();
      setQuoteReviewStatuses((prev) => ({ ...prev, [index]: 'accepted' }));
    } catch (err) {
      console.error('Error applying quote review suggestion:', err);
      setQuoteAssistError(err.response?.data?.error || err.message || 'Failed to apply suggestion');
    } finally {
      setQuoteReviewApplying((prev) => ({ ...prev, [index]: false }));
    }
  };

  const rejectQuoteReviewSuggestion = (index) => {
    setQuoteReviewStatuses((prev) => ({ ...prev, [index]: 'rejected' }));
  };

  const renderQuestionInput = (question) => {
    const value = quoteAssistAnswers[question.id] ?? '';

    if (question.type === 'textarea') {
      return (
        <TextField
          fullWidth
          multiline
          minRows={3}
          label={question.label}
          value={value}
          onChange={(event) => updateQuoteAnswer(question.id, event.target.value)}
          helperText={question.help_text || ''}
          placeholder={question.placeholder || ''}
          required={!!question.required}
          margin="normal"
        />
      );
    }

    if (question.type === 'number') {
      return (
        <TextField
          fullWidth
          type="number"
          label={question.label}
          value={value}
          onChange={(event) => updateQuoteAnswer(question.id, event.target.value)}
          helperText={question.help_text || ''}
          placeholder={question.placeholder || ''}
          required={!!question.required}
          margin="normal"
        />
      );
    }

    if (question.type === 'date') {
      return (
        <TextField
          fullWidth
          type="date"
          label={question.label}
          value={value}
          onChange={(event) => updateQuoteAnswer(question.id, event.target.value)}
          helperText={question.help_text || ''}
          required={!!question.required}
          margin="normal"
          InputLabelProps={{ shrink: true }}
        />
      );
    }

    if (question.type === 'select') {
      const options = question.options || [];
      const hasCustomValue = Boolean(value) && !options.includes(value);
      const customMode = Boolean(quoteAssistCustomModes[question.id]) || hasCustomValue;

      return (
        <Box sx={{ mt: 2 }}>
          <TextField
            fullWidth
            select
            label={question.label}
            value={customMode ? '__custom__' : value}
            onChange={(event) => {
              const selectedValue = event.target.value;
              if (selectedValue === '__custom__') {
                setQuoteAssistCustomModes((prev) => ({ ...prev, [question.id]: true }));
                if (options.includes(value)) {
                  updateQuoteAnswer(question.id, '');
                }
                return;
              }

              setQuoteAssistCustomModes((prev) => ({ ...prev, [question.id]: false }));
              updateQuoteAnswer(question.id, selectedValue);
            }}
            helperText={question.help_text || ''}
            required={!!question.required}
            margin="normal"
          >
            <MenuItem value="">
              <em>Select an option</em>
            </MenuItem>
            {options.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
            <MenuItem value="__custom__">Other (enter custom value)</MenuItem>
          </TextField>

          {customMode && (
            <TextField
              fullWidth
              label="Custom value"
              value={hasCustomValue ? value : ''}
              onChange={(event) => updateQuoteAnswer(question.id, event.target.value)}
              placeholder={question.placeholder || 'Enter your own value'}
              margin="normal"
            />
          )}
        </Box>
      );
    }

    if (question.type === 'boolean') {
      return (
        <FormControlLabel
          sx={{ mt: 2, mb: 1 }}
          control={
            <Switch
              checked={Boolean(value)}
              onChange={(event) => updateQuoteAnswer(question.id, event.target.checked)}
            />
          }
          label={question.label}
        />
      );
    }

    return (
      <TextField
        fullWidth
        label={question.label}
        value={value}
        onChange={(event) => updateQuoteAnswer(question.id, event.target.value)}
        helperText={question.help_text || ''}
        placeholder={question.placeholder || ''}
        required={!!question.required}
        margin="normal"
      />
    );
  };

  const quoteAssistDisabledReason = null;

  const canUseQuoteAssist = isEditingAllowed;
  const isQuoteReviewMode = quoteAssistMode === 'review';
  
  return (
    <Box sx={{ p: { xs: 1, md: 2 }, overflowX: 'hidden' }}>
      {/* Existing Project Items Section */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' },
        mb: 2,
        gap: 2
      }}>
        {/* Hide/Show Prices Toggle Switch - Left Side */}
        {items.length > 0 && (
          <FormControlLabel
            control={
              <Switch
                checked={hidesPrices}
                onChange={(e) => {
                  const next = e.target.checked;
                  setHidesPrices(next);
                  try {
                    localStorage.setItem('projects_hideValues', JSON.stringify(next));
                  } catch (err) {
                    console.warn('Could not write hideValues to localStorage', err);
                  }
                }}
              />
            }
            label={hidesPrices ? 'Show Values' : 'Hide Values'}
            sx={{ 
              m: 0,
              '& .MuiFormControlLabel-label': {
                fontSize: '0.9rem',
                fontWeight: 500
              }
            }}
          />
        )}
        
        <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
          <Tooltip title={quoteAssistDisabledReason || ''}>
            <span>
              <Button
                variant="outlined"
                onClick={openQuoteAssist}
                disabled={!canUseQuoteAssist || quoteAssistLoading}
                startIcon={<AutoAwesomeIcon />}
                sx={{ minWidth: { xs: 'auto', sm: '140px' } }}
              >
                {quoteAssistLoading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Loading...
                  </>
                ) : items.length === 0 ? (
                  'Quote Assist'
                ) : (
                  'Quote Review'
                )}
              </Button>
            </span>
          </Tooltip>

          {/* Export PDF Button */}
          {items.length > 0 && (
            <Button
              variant="outlined"
              startIcon={<PictureAsPdfIcon />}
              onClick={handlePdfExport}
              disabled={exportLoading || loadingTerms}
              sx={{ minWidth: { xs: 'auto', sm: '180px' } }}
            >
              {exportLoading || loadingTerms ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  {loadingTerms ? 'Loading...' : 'Exporting...'}
                </>
              ) : (
                `Export ${projectStatus === 'Quote' ? 'Quote' : 'Sales Order'} PDF`
              )}
            </Button>
          )}
        </Box>
      </Box>
      
      {/* Mobile view - Cards */}
      {isMobile ? (
  <Box sx={{ overflowX: 'hidden' }}>
          {reorderLoading && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                Updating item order...
              </Box>
            </Alert>
          )}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={items.map(item => item.item_id)} strategy={verticalListSortingStrategy}>
              {items.map((item) => (
                <ItemCard key={item.item_id} item={item} />
              ))}
            </SortableContext>
          </DndContext>
          {items.length === 0 && (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No items added yet
              </Typography>
            </Paper>
          )}
          {items.length > 0 && !hidesPrices && (
            <Paper sx={{ p: 2, mt: 2 }}>
              <Box sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body1">
                    Net Total:
                  </Typography>
                  <Typography variant="body1">
                    {formatCurrency(calculateNetTotal())}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    VAT (20%):
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatCurrency(calculateTax())}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                borderTop: 1,
                borderColor: 'divider',
                pt: 1
              }}>
                <Typography variant="h6">
                  Gross Total:
                </Typography>
                <Typography variant="h6">
                  {formatCurrency(calculateGrandTotal())}
                </Typography>
              </Box>
            </Paper>
          )}
        </Box>
      ) : (
        /* Desktop view - Table */
        <Box>
          {reorderLoading && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                Updating item order...
              </Box>
            </Alert>
          )}
          <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  {isEditingAllowed && <TableCell width="40px" />}
                  <TableCell>Name</TableCell>
                  <TableCell align="right">Quantity</TableCell>
                  <TableCell align="right">Units</TableCell>
                  {!hidesPrices && <TableCell align="right">Price</TableCell>}
                  {!hidesPrices && <TableCell align="right">Total</TableCell>}
                  {isEditingAllowed && <TableCell align="center">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext items={items.map(item => item.item_id)} strategy={verticalListSortingStrategy}>
                    {items.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={isEditingAllowed ? (hidesPrices ? 5 : 7) : (hidesPrices ? 4 : 6)} align="center">
                          No items added yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      items.map((item) => (
                        <SortableTableRow key={item.item_id} id={item.item_id} isDragging={draggedItem?.item_id === item.item_id}>
                          {isEditingAllowed && (
                            <TableCell>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  cursor: 'grab',
                                  '&:active': { cursor: 'grabbing' },
                                }}
                              >
                                <DragIndicatorIcon color="action" />
                              </Box>
                            </TableCell>
                          )}
                          <TableCell sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {item.name}
                          </TableCell>
                          <TableCell align="right">{item.quantity}</TableCell>
                          <TableCell align="right">{item.units || '-'}</TableCell>
                          {!hidesPrices && <TableCell align="right">{formatCurrency(item.list_price)}</TableCell>}
                          {!hidesPrices && <TableCell align="right">{formatCurrency(calculateLineTotal(item))}</TableCell>}
                          {isEditingAllowed && (
                            <TableCell align="center">
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditItem(item)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteItem(item)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          )}
                        </SortableTableRow>
                      ))
                    )}
                  </SortableContext>
                </DndContext>
                {items.length > 0 && !hidesPrices && (
                  <>
                    <TableRow>
                      <TableCell colSpan={isEditingAllowed ? 5 : 4} align="right" sx={{ fontWeight: 'normal' }}>
                        Net Total:
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'normal' }}>
                        {formatCurrency(calculateNetTotal())}
                      </TableCell>
                      {isEditingAllowed && <TableCell />}
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={isEditingAllowed ? 5 : 4} align="right" sx={{ fontWeight: 'normal' }}>
                        VAT (20%):
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'normal' }}>
                        {formatCurrency(calculateTax())}
                      </TableCell>
                      {isEditingAllowed && <TableCell />}
                    </TableRow>
                    <TableRow sx={{ '& td': { borderTop: 2, borderColor: 'divider' } }}>
                      <TableCell colSpan={isEditingAllowed ? 5 : 4} align="right" sx={{ fontWeight: 'bold' }}>
                        Gross Total:
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(calculateGrandTotal())}
                      </TableCell>
                      {isEditingAllowed && <TableCell />}
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {isEditingAllowed && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: { xs: 'stretch', sm: 'flex-end' },
            mt: 2,
          }}
        >
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddItem}
            fullWidth={isMobile}
            sx={{ minWidth: { sm: '160px' } }}
          >
            Add Item
          </Button>
        </Box>
      )}

      {/* Survey Notes Section */}
      <Box sx={{ mt: 4 }}>
        <Divider sx={{ mb: 3 }} />
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'flex-end',
          mb: 2
        }}>
          {hasUnsavedChanges && (
            <Chip 
              label="Unsaved changes" 
              color="warning" 
              size="small"
              sx={{ ml: 2 }}
            />
          )}
          {surveyNotesLoading && (
            <CircularProgress size={20} sx={{ ml: 2 }} />
          )}
        </Box>

        {surveyNotesError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {surveyNotesError}
          </Alert>
        )}

        {surveyNotesSavedMessage && !hasUnsavedChanges && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {surveyNotesSavedMessage}
          </Alert>
        )}

        <Paper sx={{ p: 2 }}>
          {/* Microphone Button - Prominent and Visible */}
          <Box sx={{ mb: 2 }}>
            <Tooltip 
              title={surveyNotesSpeechSupported ? (surveyNotesIsListening ? 'Stop listening' : 'Start microphone dictation') : 'Speech-to-text is not supported on this browser'}
            >
              <Button
                onClick={toggleSurveyNotesListening}
                disabled={!surveyNotesSpeechSupported || surveyNotesLoading}
                variant={surveyNotesIsListening ? 'contained' : 'outlined'}
                color={surveyNotesIsListening ? 'error' : 'primary'}
                size="large"
                startIcon={surveyNotesIsListening ? <MicIcon /> : <MicOffIcon />}
                sx={{ 
                  minWidth: 180,
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  animation: surveyNotesIsListening ? 'pulse 1.5s ease-in-out infinite' : 'none',
                  '@keyframes pulse': {
                    '0%': {
                      boxShadow: '0 0 0 0 rgba(211, 47, 47, 0.7)'
                    },
                    '70%': {
                      boxShadow: '0 0 0 10px rgba(211, 47, 47, 0)'
                    },
                    '100%': {
                      boxShadow: '0 0 0 0 rgba(211, 47, 47, 0)'
                    }
                  }
                }}
              >
                {surveyNotesIsListening ? 'Stop Recording' : 'Start Recording'}
              </Button>
            </Tooltip>
            {surveyNotesIsListening && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <Box 
                  sx={{ 
                    width: 10, 
                    height: 10, 
                    borderRadius: '50%', 
                    backgroundColor: 'error.main',
                    animation: 'blink 1s ease-in-out infinite',
                    '@keyframes blink': {
                      '0%, 100%': {
                        opacity: 1
                      },
                      '50%': {
                        opacity: 0.5
                      }
                    }
                  }} 
                />
                <Typography variant="body2" color="error.main" sx={{ fontWeight: 500 }}>
                  Listening...
                </Typography>
              </Box>
            )}
            {surveyNotesSpeechError && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                {surveyNotesSpeechError}
              </Typography>
            )}
          </Box>

          <TextField
            fullWidth
            multiline
            rows={8}
            variant="outlined"
            placeholder="Enter survey notes, observations, site conditions, measurements, or any other relevant information about the project site..."
            value={surveyNotes}
            onChange={handleSurveyNotesChange}
            disabled={surveyNotesLoading}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: '0.95rem',
                lineHeight: 1.6,
              }
            }}
          />
          
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mt: 2,
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1
          }}>
            <Typography variant="caption" color="text.secondary">
              Changes are only stored when you select Save Notes.
            </Typography>

            <Button
              variant="contained"
              size="small"
              onClick={saveSurveyNotes}
              disabled={surveyNotesLoading || !hasUnsavedChanges}
              startIcon={surveyNotesLoading ? <CircularProgress size={16} /> : null}
            >
              Save Notes
            </Button>
          </Box>
        </Paper>

        {!isMobile && (
          <Accordion
            expanded={historyExpanded}
            onChange={handleHistoryExpandedChange}
            sx={{ mt: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HistoryIcon color="action" />
                <Typography sx={{ fontWeight: 600 }}>Version history</Typography>
                {historyLoaded && (
                  <Chip label={surveyNotesHistory.length} size="small" variant="outlined" />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              {historyLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              )}

              {historyError && (
                <Alert
                  severity="error"
                  action={(
                    <Button color="inherit" size="small" onClick={loadSurveyNotesHistory}>
                      Retry
                    </Button>
                  )}
                >
                  {historyError}
                </Alert>
              )}

              {!historyLoading && !historyError && historyLoaded && surveyNotesHistory.length === 0 && (
                <Typography color="text.secondary" sx={{ py: 2 }}>
                  No saved versions yet.
                </Typography>
              )}

              {!historyLoading && !historyError && surveyNotesHistory.length > 0 && (
                <Stack spacing={1.5}>
                  {surveyNotesHistory.map((entry, index) => (
                    <Paper key={entry.history_id} variant="outlined" sx={{ p: 2 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          gap: 2,
                          mb: 1,
                        }}
                      >
                        <Box>
                          <Typography variant="subtitle2">
                            {entry.change_reason === 'legacy-import'
                              ? 'Existing note when history was enabled'
                              : (index === 0 ? 'Latest saved version' : 'Previous version')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {entry.change_reason === 'legacy-import'
                              ? `Imported ${formatHistoryDate(entry.updated_at)}`
                              : `${entry.updated_by_worker_name || 'Unknown user'} · ${formatHistoryDate(entry.updated_at)}`}
                            {entry.source && entry.source !== 'existing' ? ` · ${entry.source}` : ''}
                          </Typography>
                        </Box>
                        <Button size="small" onClick={() => setHistoryDialogEntry(entry)}>
                          View
                        </Button>
                      </Box>
                      <Typography
                        variant="body2"
                        color={entry.notes ? 'text.primary' : 'text.secondary'}
                        sx={{
                          whiteSpace: 'pre-wrap',
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {entry.notes || 'Notes were cleared'}
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
              )}
            </AccordionDetails>
          </Accordion>
        )}

        <Dialog
          open={Boolean(historyDialogEntry)}
          onClose={() => setHistoryDialogEntry(null)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Saved survey notes</DialogTitle>
          <DialogContent dividers>
            {historyDialogEntry && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {historyDialogEntry.updated_by_worker_name || 'Unknown user'} ·{' '}
                  {formatHistoryDate(historyDialogEntry.updated_at)}
                </Typography>
                <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                  {historyDialogEntry.notes || 'Notes were cleared'}
                </Typography>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setHistoryDialogEntry(null)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
      
      {/* Existing Create/Edit Item Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleDialogClose} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {dialogMode === 'create' ? 'Add New Item' : 'Edit Item'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            {/* Saved Items Search - Only show when creating new items */}
            {dialogMode === 'create' && (
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  label="Search saved items"
                  value={savedItemsSearch}
                  onChange={(e) => handleSavedItemsSearch(e.target.value)}
                  placeholder="Start typing to search for saved items..."
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                  margin="normal"
                />
                
                {/* Saved Items Results */}
                {showSavedItems && (
                  <Box sx={{ mt: 1, mb: 2 }}>
                    {savedItemsLoading ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        <Typography variant="body2">Searching...</Typography>
                      </Box>
                    ) : savedItems.length > 0 ? (
                      <Paper sx={{ maxHeight: 200, overflow: 'auto', border: 1, borderColor: 'divider' }}>
                        {savedItems.map((savedItem) => (
                          <Box
                            key={savedItem.item_id}
                            sx={{
                              p: 2,
                              borderBottom: 1,
                              borderColor: 'divider',
                              cursor: 'pointer',
                              '&:hover': { bgcolor: 'action.hover' },
                              '&:last-child': { borderBottom: 0 }
                            }}
                            onClick={() => handleSavedItemSelect(savedItem)}
                          >
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {savedItem.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Units: {savedItem.units}
                            </Typography>
                          </Box>
                        ))}
                      </Paper>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                        No saved items found matching "{savedItemsSearch}"
                      </Typography>
                    )}
                  </Box>
                )}
                
                <Divider sx={{ my: 2 }} />
              </Box>
            )}
            
            <TextField
              fullWidth
              label="Name"
              name="name"
              value={itemData.name}
              onChange={handleItemDataChange}
              margin="normal"
              required
              multiline
              minRows={2}
              maxRows={10}
            />
            <TextField
              fullWidth
              label="Units"
              name="units"
              value={itemData.units}
              onChange={handleItemDataChange}
              margin="normal"
              required
              placeholder="e.g., m², linear metres, each, kg, etc."
            />
            <TextField
              fullWidth
              label="Quantity"
              name="quantity"
              type="number"
              value={itemData.quantity}
              onChange={handleItemDataChange}
              margin="normal"
              required
              inputProps={{ min: 1, step: 1 }}
            />
            <TextField
              fullWidth
              label="Price (£)"
              name="list_price"
              type="number"
              value={itemData.list_price}
              onChange={handleItemDataChange}
              margin="normal"
              required
              inputProps={{ min: 0, step: 0.01 }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, md: 3 }, gap: 1 }}>
          <Button onClick={handleDialogClose} fullWidth={isMobile}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveItem}
            variant="contained"
            color="primary"
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
            fullWidth={isMobile}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Terms and Conditions Dialog */}
      <Dialog
        open={termsDialogOpen}
        onClose={handleTermsDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Terms and Conditions</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            label="Custom Terms and Conditions"
            value={customTerms}
            onChange={handleCustomTermsChange}
            helperText="You can customize the terms and conditions for this export. Leave blank to use the default terms."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleTermsDialogClose} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleQuotePdfGeneration}
            variant="contained"
            color="primary"
            disabled={exportLoading}
            startIcon={exportLoading && <CircularProgress size={20} />}
          >
            Export as Quote PDF
          </Button>
        </DialogActions>
      </Dialog>

      {/* Quote Assist Dialog */}
      <Dialog
        open={quoteAssistOpen}
        onClose={quoteAssistLoading ? undefined : resetQuoteAssistState}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            position: 'relative',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          {isQuoteReviewMode ? (
            <>
              <Typography variant="h6" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                <AutoAwesomeIcon fontSize="small" />
                AI Quote Review
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Instant review of survey notes and quote line items with important suggested changes.
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                AI Quote Assist
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Review the survey notes and project photos, answer quick follow-ups, and generate project items.
              </Typography>
            </>
          )}
        </DialogTitle>
        {!isQuoteReviewMode && quoteAssistLoadingPhase === 'questions' && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 10,
              bgcolor: 'rgba(255, 255, 255, 0.88)',
              backdropFilter: 'blur(3px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: 3,
              textAlign: 'center',
            }}
          >
            <Stack spacing={2} alignItems="center" sx={{ maxWidth: 420 }}>
              <CircularProgress size={34} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Considering whether any more information is needed
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Reviewing the request, notes, and project context before deciding whether to ask follow-up questions.
              </Typography>
            </Stack>
          </Box>
        )}
        <DialogContent sx={{ pt: 1 }}>
          <Alert severity="info" variant="outlined" sx={{ mb: 2, mt: 1 }}>
            <Typography variant="caption">
              <b>Disclaimer:</b> Struxor makes no guarantees regarding the quality or
              accuracy of AI-generated outputs. All outputs should be carefully reviewed
              before use. By continuing to use this tool you acknowledge and accept this.
            </Typography>
          </Alert>
          {quoteAssistError && (
            <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
              {quoteAssistError}
            </Alert>
          )}
          {isQuoteReviewMode ? (
            <>
              {quoteAssistStep === 'generating' && (
                <Box sx={{ py: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                  <CircularProgress size={28} />
                  <Typography>Reviewing quote...</Typography>
                </Box>
              )}

              {quoteAssistStep !== 'generating' && (
                <Stack spacing={2}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Overall review
                    </Typography>
                    <Typography variant="body1">
                      {quoteReviewSummary || 'No major issues found in this quote.'}
                    </Typography>
                  </Paper>

                  {(quoteReviewSuggestions || []).length === 0 ? (
                    <Alert severity="success">No important line changes were suggested.</Alert>
                  ) : (
                    (quoteReviewSuggestions || []).map((suggestion, index) => {
                      const status = quoteReviewStatuses[index] || 'pending';
                      const isApplying = Boolean(quoteReviewApplying[index]);

                      return (
                        <Paper key={`quote-review-${index}`} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                            <Chip
                              size="small"
                              color={suggestion.action === 'remove' ? 'error' : suggestion.action === 'add' ? 'success' : 'primary'}
                              label={String(suggestion.action || '').toUpperCase()}
                            />
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`${String(suggestion.importance || 'medium').toUpperCase()} impact`}
                            />
                            {status === 'accepted' && <Chip size="small" color="success" label="Accepted" />}
                            {status === 'rejected' && <Chip size="small" color="default" label="Rejected" />}
                          </Stack>

                          <Typography variant="body2" sx={{ mb: 1.5 }}>
                            {suggestion.reason}
                          </Typography>

                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                              gap: 1.5,
                            }}
                          >
                            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
                              <Typography variant="caption" color="text.secondary">Before</Typography>
                              {suggestion.before ? (
                                <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                  <Typography variant="body2"><strong>Name:</strong> {formatReviewValue(suggestion.before.name)}</Typography>
                                  <Typography variant="body2"><strong>Units:</strong> {formatReviewValue(suggestion.before.units)}</Typography>
                                  <Typography variant="body2"><strong>Qty:</strong> {formatReviewValue(suggestion.before.quantity)}</Typography>
                                  <Typography variant="body2"><strong>Price:</strong> {formatCurrency(Number(suggestion.before.list_price || 0))}</Typography>
                                </Stack>
                              ) : (
                                <Typography variant="body2" sx={{ mt: 0.5 }} color="text.secondary">Not applicable</Typography>
                              )}
                            </Paper>

                            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
                              <Typography variant="caption" color="text.secondary">After</Typography>
                              {suggestion.after ? (
                                <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                  <Typography variant="body2"><strong>Name:</strong> {formatReviewValue(suggestion.after.name)}</Typography>
                                  <Typography variant="body2"><strong>Units:</strong> {formatReviewValue(suggestion.after.units)}</Typography>
                                  <Typography variant="body2"><strong>Qty:</strong> {formatReviewValue(suggestion.after.quantity)}</Typography>
                                  <Typography variant="body2"><strong>Price:</strong> {formatCurrency(Number(suggestion.after.list_price || 0))}</Typography>
                                </Stack>
                              ) : (
                                <Typography variant="body2" sx={{ mt: 0.5 }} color="text.secondary">Remove this item</Typography>
                              )}
                            </Paper>
                          </Box>

                          {status === 'pending' && (
                            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => applyQuoteReviewSuggestion(suggestion, index)}
                                disabled={isApplying}
                              >
                                {isApplying ? 'Applying...' : 'Accept'}
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => rejectQuoteReviewSuggestion(index)}
                                disabled={isApplying}
                              >
                                Reject
                              </Button>
                            </Stack>
                          )}
                        </Paper>
                      );
                    })
                  )}
                </Stack>
              )}
            </>
          ) : (
            <>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  mb: 2,
                  borderRadius: 2,
                  bgcolor: 'background.default',
                }}
              >
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    label="1. Request"
                    color={quoteAssistStep === 'request' ? 'primary' : 'default'}
                    variant={quoteAssistStep === 'request' ? 'filled' : 'outlined'}
                  />
                  <Chip
                    size="small"
                    label="2. Questions"
                    color={quoteAssistStep === 'questions' ? 'primary' : 'default'}
                    variant={quoteAssistStep === 'questions' ? 'filled' : 'outlined'}
                  />
                  <Chip
                    size="small"
                    label="3. Generate"
                    color={quoteAssistStep === 'generating' ? 'primary' : 'default'}
                    variant={quoteAssistStep === 'generating' ? 'filled' : 'outlined'}
                  />
                </Stack>
              </Paper>

              {quoteAssistSpeechError && quoteAssistStep === 'request' && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {quoteAssistSpeechError}
                </Alert>
              )}

              {quoteAssistStep === 'loading' && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 4,
                    borderRadius: 2,
                    minHeight: 260,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                  }}
                >
                  <CircularProgress size={32} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Gathering project context
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 420 }}>
                    Loading survey notes, photos, and plan images for this quote before you review and generate items.
                  </Typography>
                </Paper>
              )}

              {quoteAssistStep === 'request' && (
                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                  {quoteAssistLoading && quoteAssistLoadingPhase === 'context' && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Loading project photos and plans in the background. You can review or edit the request now.
                    </Alert>
                  )}

                  <TextField
                    fullWidth
                    multiline
                    minRows={5}
                    label="What should this quote include?"
                    value={quoteAssistContext.request_text || ''}
                    onChange={(event) => {
                      const nextText = event.target.value;
                      setQuoteAssistContext((prev) => ({ ...prev, request_text: nextText }));
                    }}
                    placeholder="Describe the work and scope for this quote"
                    InputProps={{
                      endAdornment: (
                        <Tooltip title={quoteAssistSpeechSupported ? (quoteAssistIsListening ? 'Stop dictation' : 'Use microphone') : 'Speech-to-text is not supported on this browser'}>
                          <span>
                            <IconButton
                              onClick={toggleQuoteAssistListening}
                              disabled={!quoteAssistSpeechSupported || quoteAssistLoading}
                              color={quoteAssistIsListening ? 'primary' : 'default'}
                              aria-label={quoteAssistIsListening ? 'Stop dictation' : 'Start dictation'}
                            >
                              {quoteAssistIsListening ? <MicOffIcon /> : <MicIcon />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                      },
                    }}
                  />

                  {quoteAssistSpeechSupported && quoteAssistIsListening && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Listening... tap the microphone again to stop.
                    </Typography>
                  )}

                  <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
                    Photos & PDFs for context
                  </Typography>

                  <Box sx={{ mb: 2 }}>
                    <input
                      type="file"
                      multiple
                      accept="image/*,.pdf,application/pdf"
                      onChange={addQuoteAssistImage}
                      style={{ display: 'none' }}
                      id="quote-assist-image-input"
                    />
                    <label htmlFor="quote-assist-image-input">
                      <Button
                        variant="outlined"
                        component="span"
                        fullWidth
                        sx={{ mb: (quoteAssistContext.request_images || []).length > 0 ? 2 : 0 }}
                      >
                        + Add Photos or PDFs
                      </Button>
                    </label>
                    {(quoteAssistContext.request_images || []).length > 0 && (
                      <Button
                        color="error"
                        onClick={clearQuoteAssistImages}
                        sx={{ mt: 1 }}
                      >
                        Remove all files
                      </Button>
                    )}
                  </Box>

                  {(quoteAssistContext.request_images || []).length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No files selected.
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 1.5 }}>
                      {(quoteAssistContext.request_images || []).map((image, index) => (
                        <Paper key={`${image.file_id || image.file_name || 'img'}-${index}`} variant="outlined" sx={{ p: 1, borderRadius: 2 }}>
                          <Box
                            component="img"
                            src={image.url || image.data_url}
                            alt={image.file_name || `image-${index + 1}`}
                            sx={{ width: '100%', height: 80, objectFit: 'cover', borderRadius: 1, mb: 1 }}
                          />
                          <Typography
                            variant="caption"
                            sx={{ display: 'block', mb: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          >
                            {image.file_name || 'Project image'}
                          </Typography>
                          <Button
                            size="small"
                            color="error"
                            onClick={() => removeQuoteAssistImage(index)}
                          >
                            Remove
                          </Button>
                        </Paper>
                      ))}
                    </Box>
                  )}
                </Paper>
              )}

              {quoteAssistStep === 'questions' && (
                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Please answer these quick questions.
                  </Typography>
                  {(quoteAssistQuestions || []).map((question, index) => (
                    <Box
                      key={question.id}
                      sx={{
                        p: 1.5,
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1.5,
                        mt: index === 0 ? 0 : 1.5,
                      }}
                    >
                      {renderQuestionInput(question)}
                    </Box>
                  ))}
                </Paper>
              )}

              {quoteAssistStep === 'generating' && (
                <Box sx={{ py: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                  <CircularProgress size={28} />
                  <Typography>Generating project items from your quote request...</Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={resetQuoteAssistState} disabled={quoteAssistLoading}>
            {isQuoteReviewMode ? 'Close' : 'Cancel'}
          </Button>
          {!isQuoteReviewMode && quoteAssistStep === 'request' && (
            <Button
              onClick={startQuoteAssist}
              variant="contained"
              disabled={quoteAssistLoading || !(quoteAssistContext.request_text || '').trim()}
            >
              {quoteAssistLoading ? 'Starting...' : 'Start'}
            </Button>
          )}
          {!isQuoteReviewMode && quoteAssistStep === 'questions' && (
            <Button
              onClick={continueQuoteAssist}
              variant="contained"
              disabled={quoteAssistLoading}
            >
              {quoteAssistLoading ? 'Continuing...' : 'Continue'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ProjectItemsTab;
